/**
 * MarkdownTable Component
 * 
 * Renders a markdown table with premium styling and Excel export functionality.
 * Uses the shared markdown library for parsing and formatting.
 */

import React, { useState, useMemo } from 'react';
import {
    parseMarkdownTable,
    isNumericHeader,
    processInlineMarkdown,
} from '@/shared/lib/markdown';
import type { ColumnAlignment } from '@/shared/lib/markdown';
import { useTranslation } from 'react-i18next';
import { ExportButton } from './components/ExportButton';
import { TableCell } from './components/TableCell';
import { useTableExport } from './hooks/useTableExport';

interface MarkdownTableProps {
    /** Raw markdown table content */
    content: string;
}

/**
 * Premium table component with:
 * - Automatic content type detection (currency, percentage, numbers)
 * - Semantic colors for different data types
 * - Gradient header with subtle styling
 * - Zebra striping and hover effects
 * - Glassmorphism export button on hover
 * - XLSX export functionality
 */
export const MarkdownTable: React.FC<MarkdownTableProps> = ({ content }) => {
    const { t } = useTranslation("common");
    const [isHovered, setIsHovered] = useState(false);

    // Parse the markdown table
    const { headers, alignments, rows } = useMemo(
        () => parseMarkdownTable(content),
        [content]
    );

    // Export functionality
    const { handleExport, isExporting, exportSuccess } = useTableExport({
        headers,
        rows,
    });

    // Fallback for invalid tables
    if (headers.length === 0) {
        return (
            <pre className="text-sm text-slate-500 dark:text-slate-400 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                {content}
            </pre>
        );
    }

    return (
        <div
            className="group relative my-4"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Export Button */}
            <ExportButton
                onExport={handleExport}
                isExporting={isExporting}
                exportSuccess={exportSuccess}
                isVisible={isHovered}
            />

            {/* Table Container */}
            <div
                className={`
                    overflow-hidden rounded-2xl
                    bg-white dark:bg-slate-900
                    border border-slate-200/80 dark:border-slate-700/50
                    shadow-sm
                    transition-all duration-300 ease-out
                    ${isHovered
                        ? 'shadow-lg shadow-slate-200/50 dark:shadow-black/20 border-slate-300/80 dark:border-slate-600/50'
                        : ''
                    }
                `}
            >
                <div className="overflow-x-auto">
                    <table className="w-full">
                        {/* Header */}
                        <thead>
                            <tr className="bg-gradient-to-r from-slate-50 to-slate-100/80 dark:from-slate-800/80 dark:to-slate-800/50">
                                {headers.map((header, i) => (
                                    <TableHeader
                                        key={i}
                                        content={header}
                                        alignment={alignments[i] || 'left'}
                                        isFirst={i === 0}
                                        isLast={i === headers.length - 1}
                                    />
                                ))}
                            </tr>
                        </thead>

                        {/* Body */}
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                            {rows.map((row, rowIndex) => (
                                <tr
                                    key={rowIndex}
                                    className={`
                                        transition-colors duration-150 ease-out
                                        ${rowIndex % 2 === 0
                                            ? 'bg-white dark:bg-slate-900'
                                            : 'bg-slate-50/50 dark:bg-slate-800/20'
                                        }
                                        hover:bg-blue-50/50 dark:hover:bg-blue-900/10
                                    `}
                                >
                                    {row.map((cell, cellIndex) => (
                                        <TableCell
                                            key={cellIndex}
                                            content={cell}
                                            alignment={alignments[cellIndex] || 'left'}
                                            isFirst={cellIndex === 0}
                                            isLast={cellIndex === row.length - 1}
                                        />
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Footer with row count */}
                <div className="px-6 py-3 bg-slate-50/50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800">
                    <p className="text-xs text-slate-400 dark:text-slate-500">
                        {rows.length} {rows.length === 1 ? t('record') : t('records')}
                    </p>
                </div>
            </div>
        </div>
    );
};

// =============================================================================
// Internal Components
// =============================================================================

interface TableHeaderProps {
    content: string;
    alignment: ColumnAlignment;
    isFirst?: boolean;
    isLast?: boolean;
}

const TableHeader: React.FC<TableHeaderProps> = ({
    content,
    alignment,
    isFirst = false,
    isLast = false,
}) => {
    // Numeric headers are right-aligned
    const isNumeric = isNumericHeader(content);
    const finalAlignment = isNumeric ? 'right' : alignment;

    const alignmentClass = {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
    }[finalAlignment];

    return (
        <th
            className={`
                px-5 py-4
                text-xs font-semibold uppercase tracking-wider
                text-slate-500 dark:text-slate-400
                border-b border-slate-200 dark:border-slate-700
                ${alignmentClass}
                ${isFirst ? 'pl-6' : ''}
                ${isLast ? 'pr-6' : ''}
            `}
            dangerouslySetInnerHTML={{ __html: processInlineMarkdown(content) }}
        />
    );
};
